import type {
  EGenerationType,
  EMessageFileSource,
  EMessageRole,
  IMessage,
  IMessageError,
} from "./chat";

export enum EChatShareAccessScope {
  ORGANIZATION = "organization",
  PROJECT = "project",
  // NOTE: Access granted per-user via the share's recipient list rather than by
  // project/organization membership — the explicit invite is the grant.
  SPECIFIC_PEOPLE = "specific_people",
}

export type TExpiryPreset = "never" | "1d" | "7d" | "30d" | "custom";

// NOTE: Bot origin exposed on shared chats. A bare source enum is sent instead
// of the raw Telegram/Teams/Slack identifiers so share links (viewable by users
// outside the original bot channel) never leak those external IDs (NSAI-3009).
export enum EBotChatSource {
  TELEGRAM = "telegram",
  TEAMS = "teams",
  SLACK = "slack",
  WHATSAPP = "whatsapp",
  MATRIX = "matrix",
}

export interface ISharedChatInfo {
  uuid: string;
  title: string;
  createdAt: string;
  // NOTE: True when the authenticated viewer owns this chat. The web app uses
  // this to redirect the owner to their native editable chat instead of the
  // read-only shared view.
  isViewerOwner: boolean;
  botSource: EBotChatSource | null;
}

export interface IChatShareRecipient {
  uuid: string;
  userUuid: string;
  userFirstName: string | null;
  userLastName: string | null;
  userEmail: string;
  // NOTE: NSAI-7736 — the IdP login name of a person with no email.
  userUsername?: string | null;
  createdAt: string;
  // NOTE: Whether the recipient can actually open the shared chat right now.
  // The invite persists across role changes on purpose, so a recipient whose
  // role lost chat_share:read stays listed but reads false here — the dialog
  // marks them instead of implying access they no longer have (NSAI-4540).
  canView: boolean;
}

export interface IChatShareResponse {
  uuid: string;
  chatUuid: string;
  accessScope: EChatShareAccessScope;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  // NOTE: Populated only for SPECIFIC_PEOPLE shares; empty for project/org scopes.
  recipients: IChatShareRecipient[];
}

export interface ICreateChatShareBody {
  accessScope: EChatShareAccessScope;
  expiresAt?: string | null;
  // NOTE: Required (non-empty) only when accessScope is SPECIFIC_PEOPLE.
  recipientUserUuids?: string[];
}

export interface IAddChatShareRecipientsBody {
  recipientUserUuids: string[];
}

export interface IRemoveChatShareRecipientsBody {
  recipientUuids: string[];
}

// NOTE: NSAI-3499 — names which live member share to revoke when a chat holds
// several. Without it the server revokes the newest one.
export interface IRevokeChatShareBody {
  accessScope?: EChatShareAccessScope;
}

export interface ISharedChatMessageFile {
  uuid: string;
  name: string;
  type: string;
  source: EMessageFileSource | null;
  imageBase64?: string;
}

export interface ISharedChatMessageModel {
  uuid: string;
  title: string;
}

export interface ISharedChatMessage {
  uuid: string;
  groupId: string;
  slotIndex: number | null;
  content: string;
  role: EMessageRole;
  done: boolean;
  generationType: EGenerationType;
  error: IMessageError | null;
  // NOTE: The persisted knowledge-base hits, under the column's own name. The
  // owner-side history endpoint renames them to `knowledgeSources` before
  // sending; this payload passes the raw column through, so `toSharedChatMessage`
  // does the renaming instead.
  sources: NonNullable<IMessage["knowledgeSources"]> | null;
  info: Record<string, unknown> | null;
  files: ISharedChatMessageFile[];
  createdAt: string;
  model: ISharedChatMessageModel | null;
}

export interface ISharedChatResponse {
  chat: ISharedChatInfo;
  messages: ISharedChatMessage[];
  hasNextPage: boolean;
}

// NOTE: NSAI-6724 — the share link behind a chat's own `/chat/<uuid>` address,
// answered only to a viewer that chat was shared with.
export interface IResolvedSharedChatLink {
  shareToken: string;
}

// NOTE: NSAI-6720 — the project the owner's own chat lives in, for a
// `/chat/<uuid>` address opened while another project is active.
export interface IResolvedOwnChatProject {
  projectUuid: string;
}

// NOTE: NSAI-5823 — public (no-account) chat share. Distinct from the scopes
// above in who it reaches: those grant access to an NSAI user, this one to
// anyone holding the link and its password. Two properties are deliberate and
// load-bearing, so the shapes below carry them explicitly:
//   * the link exposes a SNAPSHOT frozen when it was created, never later
//     messages;
//   * it is TEXT ONLY — attachments appear by name, with no download path.

export interface IChatPublicShare {
  shareToken: string;
  // NOTE: Absolute URL built server-side from FRONTEND_URL, for clients with no
  // browser origin of their own (mobile). Web composes its own from
  // location.origin, which stays correct behind a custom domain.
  shareUrl: string;
  expiresAt: string | null;
  createdAt: string;
  // NOTE: Recoverable plaintext so the owner can re-read it instead of being
  // forced to regenerate. Owner-scoped responses only.
  password: string | null;
  // NOTE: How many messages the link exposes. Fixed at creation, so the owner
  // can check the snapshot against what they meant to share.
  sharedMessageCount: number;
}

// NOTE: Creation always knows the plaintext it just generated.
export interface ICreateChatPublicShareResponse extends IChatPublicShare {
  password: string;
}

export interface ICreateChatPublicShareBody {
  expiresAt?: string | null;
}

// NOTE: The anonymous pre-password call answers only this — nothing about the
// chat behind the link is revealed before the password is entered.
export interface IChatPublicShareStatusResponse {
  requiresPassword: true;
}

export interface IChatPublicShareVerifyResponse {
  accessToken: string;
  expiresInSeconds: number;
}

// NOTE: Thinner than ISharedChatInfo on purpose: an anonymous viewer has no
// account (so no `isViewerOwner`) and the chat's bot origin is internal routing
// detail that the public must not see.
export interface IPublicSharedChatInfo {
  title: string;
  createdAt: string;
  // NOTE: When the snapshot was taken, so the viewer can be told plainly that
  // they are reading the conversation as it stood at that moment.
  sharedAt: string;
}

export interface IPublicSharedChatResponse {
  chat: IPublicSharedChatInfo;
  messages: ISharedChatMessage[];
  hasNextPage: boolean;
}

// NOTE: Params for the anonymous paginated read. The access token travels as an
// explicit argument (not ambient session state) because the viewer has no NSAI
// session for the client to read it from.
export interface IGetPublicSharedChatParams {
  shareToken: string;
  accessToken: string;
  limit: number;
  offset: number;
}
